import type { SavedSearchSummary } from '../../types/saved-search';
import type { FacetValuesPerSearch } from './merge-facet-values-with-recency-conflict-bias';
import type { PreparedWeightedSearchSummary } from './prepare-weighted-search-summaries-for-inference';

export const RECENT_SEARCH_LOCATION_KEY = '__location__';

function normalizeAttr(attribute: string): string {
  return String(attribute).replace(/_/g, '').toLowerCase();
}

/** Maps URL / InstantSearch attribute names to stable keys used when merging across searches. */
export function canonicalizeSearchFacetAttr(raw: string): string {
  const a = normalizeAttr(raw);
  if (a === 'company' || a === 'companyid') return 'companyid';
  if (a === 'industry' || a === 'companyindustry') return 'industry';
  return a;
}

export function normalizeSearchLocationToken(s: string): string {
  return s.toLowerCase().replace(/,/g, ' ').replace(/\s+/g, ' ').trim();
}

function facetsByCanonical(summary: SavedSearchSummary): Map<string, Set<string>> {
  const m = new Map<string, Set<string>>();
  for (const f of summary.facets) {
    const c = canonicalizeSearchFacetAttr(f.attribute);
    if (c === 'location') continue;
    if (!m.has(c)) m.set(c, new Set());
    for (const val of f.values) {
      const v = String(val).trim();
      if (!v) continue;
      m.get(c)!.add(v.toLowerCase());
    }
  }
  return m;
}

function locationValueSet(summary: SavedSearchSummary): Set<string> {
  return new Set(
    summary.locationValues.map((x) => normalizeSearchLocationToken(String(x))).filter(Boolean)
  );
}

export function facetSetForCanonicalSummary(summary: SavedSearchSummary, canonical: string): Set<string> {
  if (canonical === RECENT_SEARCH_LOCATION_KEY) return locationValueSet(summary);
  return facetsByCanonical(summary).get(canonical) ?? new Set();
}

export function snapshotsForCanonicalPrepared(
  prepared: PreparedWeightedSearchSummary[],
  canonical: string
): FacetValuesPerSearch[] {
  return prepared.map((p, rank) => ({
    searchRank: rank,
    searchWeight: p.w,
    values: facetSetForCanonicalSummary(p.summary, canonical),
  }));
}

export function collectAllCanonicalFacetKeys(prepared: Array<{ summary: SavedSearchSummary }>): string[] {
  const keys = new Set<string>([RECENT_SEARCH_LOCATION_KEY]);
  for (const p of prepared) {
    for (const f of p.summary.facets) {
      const c = canonicalizeSearchFacetAttr(f.attribute);
      if (c !== 'location') keys.add(c);
    }
  }
  return [...keys];
}
